import { t } from 'i18next';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { getAccountTimeFormat } from 'shared/utils';
import {
  AccountInfoType,
  AllUsersType,
  ColumnSettingsType,
  FilterViewType,
  FormServFieldType,
  McrContactType,
  UserType,
} from '../../../shared/types';
import { FREEMIUM_CHECKBOX_TYPES } from '../../../shared/constants';

export const LEFT_PANE_FIELD_NAMES: string[] = [
  'contact_status',
  'work_number',
  'mobile_number',
  'company_name',
  'company_website',
  'designation',
];

export const getContactsColumnSettings = ({
  allUsers,
  fields = [],
  accountInfo,
  userInfo,
}: {
  allUsers?: AllUsersType;
  fields?: FormServFieldType[];
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
}): ColumnSettingsType => {
  const settings = {
    first_name: {
      showInitial: true,
      disableReordering: true,
    },
    last_name: {
      showInitial: true,
      disableReordering: true,
    },
    work_email: {
      showInitial: true,
    },
    contact_status: {
      showInitial: true,
    },
    creater_id: {
      accessor: ({ creater_id }) =>
        `${creater_id}` === '0'
          ? `${t('contacts.system')}`
          : allUsers && creater_id && allUsers[creater_id]
          ? `${get(allUsers, [creater_id, 'first_name'], '') || ''} ${
              get(allUsers, [creater_id, 'last_name'], '') || ''
            }`.trim()
          : `${t('contacts.unknown_user')}`,
    },
    updater_id: {
      accessor: ({ updater_id }) =>
        `${updater_id}` === '0'
          ? `${t('contacts.system')}`
          : allUsers && updater_id && allUsers[updater_id]
          ? `${get(allUsers, [updater_id, 'first_name'], '') || ''} ${
              get(allUsers, [updater_id, 'last_name'], '') || ''
            }`.trim()
          : `${t('contacts.unknown_user')}`,
    },
    created_at: {
      showInitial: true,
      accessor: ({ created_at }) => {
        return moment(new Date(created_at))
          .tz(accountInfo?.timezone || moment.tz.guess())
          .format(getAccountTimeFormat(accountInfo));
      },
    },
    updated_at: {
      showInitial: true,
      accessor: ({ updated_at }) =>
        moment(new Date(updated_at))
          .tz(accountInfo?.timezone || moment.tz.guess())
          .format(getAccountTimeFormat(accountInfo)),
    },
  };
  fields.forEach((field) => {
    if (!settings[field.name] && FREEMIUM_CHECKBOX_TYPES.includes(field.type)) {
      settings[field.name] = {
        accessor: (row) => {
          if (field.custom)
            return row.fields[field.name] ? `${t('yes')}` : `${t('no')}`;
          return row[field.name] ? `${t('yes')}` : `${t('no')}`;
        },
      };
    }
  });
  return settings;
};

export type ContactActionType =
  | 'view'
  | 'delete'
  | 'edit'
  | 'deactivate'
  | 'activate'
  | 'unblock'
  | 'block'
  | 'unsubscribe'
  | 'subscribe'
  | 'queueIn';

export const getContactMenuOptions = (
  contact: McrContactType,
  skipActionIds: string[],
  access: string[] = [],
): { id: string; name: string }[] =>
  [
    ...(access.indexOf('contact:read') > -1
      ? [
          {
            id: 'view',
            name: `${t('view')}`,
          },
          // {
          //   id: 'queueIn',
          //   name: 'Queue-In Survey',
          // },
        ]
      : []),
    ...(access.indexOf('contact:write') > -1
      ? [
          { id: 'edit', name: `${t('edit')}` },
          ...(contact?.fields?.contact_status !== 'IN_ACTIVE'
            ? [
                {
                  id: 'deactivate',
                  name: `${t('contacts.menu.deactivate.text')}`,
                },
              ]
            : []),
          ...(contact?.fields?.contact_status !== 'ACTIVE'
            ? [{ id: 'activate', name: `${t('contacts.menu.activate.text')}` }]
            : []),
          // ...(contact?.fields?.is_blocked
          //   ? [{ id: 'unblock', name: 'Unblock' }]
          //   : []),
          // ...(!contact?.fields?.is_blocked
          //   ? [{ id: 'block', name: 'Block' }]
          //   : []),
          ...(!contact?.fields?.is_unsubscribed
            ? [
                {
                  id: 'unsubscribe',
                  name: `${t('contacts.menu.unsubscribe.text')}`,
                },
              ]
            : []),
          ...(contact?.fields?.is_unsubscribed
            ? [
                {
                  id: 'subscribe',
                  name: `${t('contacts.menu.subscribe.text')}`,
                },
              ]
            : []),
          { id: 'delete', name: `${t('delete')}` },
        ]
      : []),
  ].filter((action) => !skipActionIds?.includes(action.id));

export const DefaultFilterViews: FilterViewType[] = [
  {
    id: 'all',
    name: 'sidebar.contacts.all_contacts',
    conditions: [],
  },
  // {
  //   id: 'blocked',
  //   name: 'Blocked',
  //   conditions: [
  //     {
  //       evaluate_on: 'CONTACT',
  //       field_type: 'checkbox',
  //       name: 'is_blocked',
  //       operator: 'CHECKED',
  //       value: '',
  //     },
  //   ],
  // },
  {
    id: 'active',
    name: 'responses.filter_condition.survey_state.active',
    conditions: [
      {
        evaluate_on: 'CONTACT',
        field_type: 'dropdown',
        name: 'contact_status',
        operator: 'IN',
        value: ['ACTIVE'],
      },
    ],
  },
  {
    id: 'inactive',
    name: 'responses.filter_condition.survey_state.inactive',
    conditions: [
      {
        evaluate_on: 'CONTACT',
        field_type: 'dropdown',
        name: 'contact_status',
        operator: 'IN',
        value: ['IN_ACTIVE'],
      },
    ],
  },
  {
    id: 'unsubscribed',
    name: 'filter_actions.unsubscribed',
    conditions: [
      {
        evaluate_on: 'CONTACT',
        field_type: 'checkbox',
        name: 'is_unsubscribed',
        operator: 'CHECKED',
        value: '',
      },
    ],
  },
];
